import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import type {Dispatch, SetStateAction} from 'react';
import {useSelector} from 'react-redux';

import {ProcessFlowchartEditorConfigurator} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/ProcessFlowchartEditorConfigurator';
import {FlowchartEditorMode} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/ProcessFlowchartEditorModeConstants';
import {BenchlingState} from 'redux-stores/reduceApp';
import {getStore} from 'redux-stores/Store';
import {assertNever} from 'util/Types';

import {refreshNodeDisplayNames} from '../processFlowchartConfigEditorUtils';
import {FlowchartConfigWithMetadata} from '../redux/flowchartConfigAutosaveManagerCallbacks';
import {
  createInitialExecutionDraftState,
  mergeStatusCountsIntoExecutionDraft,
} from './createInitialExecutionDraftState';
import {useExecutionFlowchartCore} from './executionFlowchartCore';
import type {LineageDataResult, LoadedFlowchartData} from './executionFlowchartData';
import {LineageDataResultType, useProcessData, useTaskGroupLineageData} from './executionFlowchartData';
import type {ExecutionFlowchartReadinessSnapshot} from './ExecutionFlowchartPaneTypes';
import {
  getExecutionFlowchartIdFromProcess,
  getFlowchartConfigIdFromProcess,
  getFlowchartConfigVersionIdFromProcess,
} from './executionFlowchartProcessIds';

const EMPTY_LINEAGE_NODE_TASK_SCHEMA_IDS: Array<string> = [];

/** Fills or updates the execution draft once template data, configurator, and lineage are ready. */
function useBootstrapExecutionDraft(args: {
  data: LoadedFlowchartData | null;
  executionEditorConfiguratorBase: ProcessFlowchartEditorConfigurator | null;
  setDraftState: Dispatch<SetStateAction<FlowchartConfigWithMetadata | null>>;
  lineageResult: LineageDataResult;
  flowchartConfigId: string | null;
  isSaveInFlight: boolean;
  onStaleDetected: () => void;
}): void {
  const {
    data,
    executionEditorConfiguratorBase,
    setDraftState,
    lineageResult,
    flowchartConfigId,
    isSaveInFlight,
    onStaleDetected,
  } = args;

  const lineageResultType = lineageResult.resultType;
  const lineageStatusCounts =
    lineageResultType === LineageDataResultType.SUCCESS ? lineageResult.data : undefined;

  useEffect(() => {
    if (data == null || executionEditorConfiguratorBase == null) {
      return;
    }
    if (lineageResultType === LineageDataResultType.PENDING) {
      return;
    }
    if (isSaveInFlight) {
      return;
    }

    // If the Apollo cache version doesn't match the Redux version (save completed but we haven't
    // refetched yet), trigger a networkOnly refetch and wait for fresh data.
    if (flowchartConfigId != null && data.flowchartConfigVersionId != null) {
      const reduxVersionId =
        getStore().getState().models.flowchartConfigs[flowchartConfigId]?.flowchartConfigMetadata
          ?.currentVersionId;
      if (reduxVersionId != null && reduxVersionId !== data.flowchartConfigVersionId) {
        onStaleDetected();
        return;
      }
    }

    setDraftState((prev) => {
      if (prev == null) {
        return createInitialExecutionDraftState(
          data,
          executionEditorConfiguratorBase.flowchartTypeName,
          lineageStatusCounts
        );
      }
      if (lineageStatusCounts != null && Object.keys(lineageStatusCounts).length > 0) {
        const merged = mergeStatusCountsIntoExecutionDraft(prev, lineageStatusCounts);
        return merged === prev ? prev : merged;
      }
      return prev;
    });
  }, [
    data,
    executionEditorConfiguratorBase,
    lineageResultType,
    lineageStatusCounts,
    setDraftState,
    flowchartConfigId,
    isSaveInFlight,
    onStaleDetected,
  ]);
}

interface UseExecutionFlowchartDraftStateResult {
  draftState: FlowchartConfigWithMetadata | null;
  setDraftState: Dispatch<SetStateAction<FlowchartConfigWithMetadata | null>>;
  clearDraft: () => void;
  readinessSnapshot: ExecutionFlowchartReadinessSnapshot;
  processIdsByNodeConfigId?: Record<string, string> | null;
}

type UseExecutionFlowchartDraftStateParams =
  | {
      resolution: 'flowchartConfig';
      flowchartConfigId: string | null;
      editorMode: FlowchartEditorMode;
    }
  | {
      resolution: 'process';
      processId: string | null;
      editorMode: FlowchartEditorMode;
      lineageRootTaskId?: string;
    };

interface DraftResolutionDerived {
  flowchartConfigId: string | null;
  flowchartConfigVersionId: string | null;
  executionFlowchartId: string | null;
  externalInaccessible: boolean;
  externalPending: boolean;
}

/** Derives draft state from flowchart config or process, with external access checks. */
function useExecutionFlowchartDraftState(
  params: UseExecutionFlowchartDraftStateParams
): UseExecutionFlowchartDraftStateResult {
  const processIdForQuery = params.resolution === 'process' ? params.processId : null;
  const processData = useProcessData(processIdForQuery);

  const derived = useMemo((): DraftResolutionDerived => {
    switch (params.resolution) {
      case 'flowchartConfig':
        return {
          flowchartConfigId: params.flowchartConfigId,
          flowchartConfigVersionId: null,
          executionFlowchartId: null,
          externalInaccessible: false,
          externalPending: false,
        };
      case 'process':
        return {
          flowchartConfigId: getFlowchartConfigIdFromProcess(processData),
          flowchartConfigVersionId: getFlowchartConfigVersionIdFromProcess(processData),
          executionFlowchartId: getExecutionFlowchartIdFromProcess(processData),
          externalInaccessible:
            processData !== undefined &&
            (processData == null || getFlowchartConfigIdFromProcess(processData) == null),
          externalPending: processData === undefined,
        };
      default:
        return assertNever(params);
    }
  }, [params, processData]);

  const [draftState, setDraftState] = useState<FlowchartConfigWithMetadata | null>(null);
  const [forceNetworkOnly, setForceNetworkOnly] = useState(false);

  const isSaveInFlight = useSelector((state: BenchlingState) =>
    derived.flowchartConfigId != null
      ? (state.models.flowchartConfigs[derived.flowchartConfigId]?.platformLiveProperties.isSaveInFlight ??
        false)
      : false
  );

  const skipFlowchartTypeConfigurator =
    (params.resolution === 'flowchartConfig' && params.flowchartConfigId == null) ||
    (params.resolution === 'process' && params.processId == null);
  const {isConfiguratorLoadError, executionEditorConfigurator, dataResult, data} = useExecutionFlowchartCore({
    flowchartConfigId: derived.flowchartConfigId,
    flowchartConfigVersionId: derived.flowchartConfigVersionId,
    executionFlowchartId: derived.executionFlowchartId,
    editorMode: params.editorMode,
    skipFlowchartTypeConfigurator,
    networkOnly: forceNetworkOnly,
  });

  const lineageNodeTaskSchemaIds = useMemo((): Array<string> => {
    const schemas = data?.availableTaskSchemas;
    if (schemas == null || schemas.length === 0) {
      return EMPTY_LINEAGE_NODE_TASK_SCHEMA_IDS;
    }
    return schemas.map((schema) => schema.id);
  }, [data]);

  const lineageProcessId = params.resolution === 'process' ? params.processId : null;
  const lineageRootTaskId = params.resolution === 'process' ? params.lineageRootTaskId : undefined;
  const lineageResult = useTaskGroupLineageData({
    flowchartId: derived.executionFlowchartId,
    flowchartProcessId: lineageProcessId,
    nodeTaskSchemaIds: lineageNodeTaskSchemaIds,
    lineageRootTaskId,
  });

  const clearDraft = useCallback((): void => {
    setDraftState(null);
  }, []);

  const handleStaleDetected = useCallback((): void => {
    setForceNetworkOnly(true);
  }, []);

  const prevEditorModeRef = useRef(params.editorMode);
  useEffect(() => {
    if (prevEditorModeRef.current !== params.editorMode) {
      prevEditorModeRef.current = params.editorMode;
      setDraftState((prev) => {
        if (prev == null) {
          return prev;
        }
        const updatedNodes = refreshNodeDisplayNames(prev.staticFlowchart.nodesStaticData, params.editorMode);
        if (updatedNodes === prev.staticFlowchart.nodesStaticData) {
          return prev;
        }
        return {
          ...prev,
          staticFlowchart: {...prev.staticFlowchart, nodesStaticData: updatedNodes},
        };
      });
    }
  }, [params.editorMode]);

  const setDraftStateAndClearNetworkOnly = useCallback(
    (updater: Parameters<typeof setDraftState>[0]) => {
      setDraftState(updater);
      // Once fresh data has been bootstrapped, clear the network-only flag so subsequent
      // queries can use the cache again.
      setForceNetworkOnly(false);
    },
    [setDraftState]
  );

  useBootstrapExecutionDraft({
    data,
    executionEditorConfiguratorBase: executionEditorConfigurator,
    setDraftState: setDraftStateAndClearNetworkOnly,
    lineageResult,
    flowchartConfigId: derived.flowchartConfigId,
    isSaveInFlight,
    onStaleDetected: handleStaleDetected,
  });

  const readinessSnapshot: ExecutionFlowchartReadinessSnapshot = useMemo(
    () => ({
      isConfiguratorLoadError,
      dataResult,
      data,
      executionEditorConfigurator,
      externalInaccessible: derived.externalInaccessible,
      externalPending: derived.externalPending,
    }),
    [
      isConfiguratorLoadError,
      dataResult,
      data,
      executionEditorConfigurator,
      derived.externalInaccessible,
      derived.externalPending,
    ]
  );

  const processIdsByNodeConfigId =
    lineageResult.resultType === LineageDataResultType.SUCCESS
      ? lineageResult.processIdsByNodeConfigId
      : null;

  return {
    draftState,
    setDraftState,
    clearDraft,
    readinessSnapshot,
    processIdsByNodeConfigId,
  };
}

/** Draft + snapshot when the root entity is a flowchart config (ACM or viewer-by-config). */
export function useExecutionFlowchartDraftStateForConfig(args: {
  flowchartConfigId: string | null;
  editorMode: FlowchartEditorMode;
}): UseExecutionFlowchartDraftStateResult {
  return useExecutionFlowchartDraftState({
    resolution: 'flowchartConfig',
    flowchartConfigId: args.flowchartConfigId,
    editorMode: args.editorMode,
  });
}

/** Draft + snapshot when the root entity is a process (PCM, viewer-by-process, task group viewer). */
export function useExecutionFlowchartDraftStateForProcess(args: {
  processId: string;
  editorMode: FlowchartEditorMode;
  lineageRootTaskId?: string;
  skip?: boolean;
}): UseExecutionFlowchartDraftStateResult {
  return useExecutionFlowchartDraftState({
    resolution: 'process',
    processId: args.skip ? null : args.processId,
    editorMode: args.editorMode,
    lineageRootTaskId: args.lineageRootTaskId,
  });
}
